/**
 * Envelope de error del BFF (contrato §3):
 *   { error: { code, message, status, details } }
 *
 * El front NUNCA muestra `message` (viene del backend, puede tener texto crudo
 * del proveedor): se muestra el mensaje del diccionario `errorMessages.ts`
 * indexado por `code`.
 */

export interface BffErrorEnvelope {
  error: {
    code: string;
    message?: string;
    status?: number;
    details?: Record<string, unknown> | null;
  };
}

export class BffError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  /** Mensaje crudo del backend. Para logs, no para la pantalla. */
  readonly backendMessage: string;

  constructor(
    code: string,
    status: number,
    backendMessage = '',
    details: Record<string, unknown> = {}
  ) {
    super(`${code} (${status})`);
    this.name = 'BffError';
    this.code = code;
    this.status = status;
    this.backendMessage = backendMessage;
    this.details = details;
    // TS 4.x + target ES5: hay que restaurar el prototipo a mano.
    Object.setPrototypeOf(this, BffError.prototype);
  }

  /** `details` con forma "campo -> código" (400 validation_failed). */
  get camposInvalidos(): Record<string, string> {
    const salida: Record<string, string> = {};
    Object.entries(this.details || {}).forEach(([campo, valor]) => {
      if (typeof valor === 'string') salida[campo] = valor;
    });
    return salida;
  }

  /** `details.reason` (422 issue_rejected). */
  get reason(): string | undefined {
    const reason = this.details?.reason;
    return typeof reason === 'string' ? reason : undefined;
  }

  /**
   * Segundos a esperar antes de reintentar. El BFF lo manda como
   * `details.retryAfter` (503 `document_not_ready`) o
   * `details.retryAfterSeconds` (409 `issue_in_progress`).
   */
  get retryAfter(): number | undefined {
    const valor = this.details?.retryAfter ?? this.details?.retryAfterSeconds;
    return typeof valor === 'number' ? valor : undefined;
  }

  /** `details.cotizacionId` (409 `issue_in_progress`, 504 `provider_timeout`). */
  get cotizacionId(): string | undefined {
    const valor = this.details?.cotizacionId;
    return typeof valor === 'string' ? valor : undefined;
  }
}

const esEnvelope = (cuerpo: unknown): cuerpo is BffErrorEnvelope =>
  !!cuerpo &&
  typeof cuerpo === 'object' &&
  typeof (cuerpo as BffErrorEnvelope).error === 'object' &&
  (cuerpo as BffErrorEnvelope).error !== null &&
  typeof (cuerpo as BffErrorEnvelope).error.code === 'string';

/**
 * Convierte cualquier falla (envelope del BFF, respuesta sin envelope, error de
 * red) en un `BffError`. Nunca tira: siempre devuelve algo mostrable.
 *
 * @param cuerpo  cuerpo de la respuesta ya parseado (o undefined)
 * @param status  status HTTP (0 si no hubo respuesta)
 */
export const parseBffError = (cuerpo: unknown, status = 0): BffError => {
  if (esEnvelope(cuerpo)) {
    const { code, message, status: statusEnvelope, details } = cuerpo.error;
    return new BffError(
      code,
      statusEnvelope || status,
      message || '',
      (details as Record<string, unknown>) || {}
    );
  }
  if (status === 0) return new BffError('network_error', 0);
  return new BffError('unexpected_error', status);
};

/** Normaliza un error de axios (o cualquier throw) a `BffError`. */
export const aBffError = (error: unknown): BffError => {
  if (error instanceof BffError) return error;
  const respuesta = (error as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (respuesta) return parseBffError(respuesta.data, respuesta.status || 0);
  return new BffError('network_error', 0);
};
